import { useFetch } from "~vue/api";
import type { LabInsightFeedbackResponse, MyWorkInsightFeed, SubmitLabInsightFeedbackRequest } from "~vue/types/labs";
import { computed, getCurrentInstance, type InjectionKey, provide, ref, watch } from "vue";

export const insightFeedActions: InjectionKey<{
    enabled: () => boolean;
    isPending: (insightId: string) => boolean;
    submit: (payload: SubmitLabInsightFeedbackRequest) => Promise<LabInsightFeedbackResponse | undefined>;
}> = Symbol("insightFeedActions");

export function useMyWorkInsightFeed(initial: () => MyWorkInsightFeed | null) {
    const updated = ref<MyWorkInsightFeed | null>(null);
    const pending = ref<SubmitLabInsightFeedbackRequest[]>([]);
    const error = ref(false);
    const committed = computed(() => updated.value ?? initial());
    const payload = ref<SubmitLabInsightFeedbackRequest | null>(null);
    const request = useFetch("/api/labs/feedback/", { immediate: false }).post(payload).json<LabInsightFeedbackResponse>();
    const refresh = useFetch("/api/labs/insights/", { immediate: false }).get().json<MyWorkInsightFeed>();
    let queue = Promise.resolve();
    watch(initial, () => {
        updated.value = null;
    });

    function nextReserve(base: MyWorkInsightFeed) {
        return base.experiments
            .flatMap((experiment) => experiment.insights)
            .filter((row) => row.insight.made_visible_at == null)
            .sort((a, b) => a.insight.order - b.insight.order || a.insight.id.localeCompare(b.insight.id))[0];
    }

    function applyActions(base: MyWorkInsightFeed, actions: SubmitLabInsightFeedbackRequest[]): MyWorkInsightFeed {
        if (!actions.length) {
            return base;
        }
        const snapshot: MyWorkInsightFeed = {
            ...base,
            experiments: base.experiments.map((experiment) => ({
                ...experiment,
                insights: experiment.insights.map((item) => ({ ...item, insight: { ...item.insight } })),
            })),
        };
        for (const action of actions) {
            const experiment = snapshot.experiments.find((exp) => exp.insights.some((item) => item.insight.id === action.insight_id));
            const item = experiment?.insights.find((item) => item.insight.id === action.insight_id);
            if (!experiment || !item || item.insight.made_visible_at == null) {
                continue;
            }
            if (action.value === 1) {
                item.feedback = 1;
                continue;
            }
            experiment.insights = experiment.insights.filter((row) => row.insight.id !== action.insight_id);
            const reserve = action.replacement_insight_id
                ? snapshot.experiments.flatMap((exp) => exp.insights).find((row) => row.insight.id === action.replacement_insight_id && row.insight.made_visible_at == null)
                : nextReserve(snapshot);
            if (reserve) {
                reserve.insight.made_visible_at = new Date().toISOString();
            }
        }
        snapshot.exhausted = !snapshot.experiments.some((exp) => exp.insights.length);
        return snapshot;
    }
    const feed = computed(() => (committed.value ? applyActions(committed.value, pending.value) : null));

    async function recover() {
        try {
            await refresh.execute();
            if (!refresh.error.value && refresh.data.value) {
                updated.value = refresh.data.value;
            }
        } finally {
            error.value = true;
        }
    }

    function submit(action: SubmitLabInsightFeedbackRequest) {
        if (pending.value.some((item) => item.insight_id === action.insight_id)) {
            return Promise.resolve(undefined);
        }
        action = { ...action, replacement_insight_id: undefined };
        error.value = false;
        pending.value = [...pending.value, action];
        const result = queue.then(async () => {
            const visible = committed.value?.experiments.some((exp) => exp.insights.some((row) => row.insight.id === action.insight_id && row.insight.made_visible_at != null));
            if (!visible) {
                pending.value = pending.value.filter((item) => item.insight_id !== action.insight_id);
                error.value = true;
                return undefined;
            }
            const dispatchedAction = {
                ...action,
                replacement_insight_id: action.value === 0 && committed.value ? nextReserve(committed.value)?.insight.id : undefined,
            };
            payload.value = dispatchedAction;
            try {
                await request.execute();
                if (request.error.value || !request.data.value) {
                    await recover();
                    return undefined;
                }
                const response = request.data.value;
                if (committed.value) {
                    updated.value = applyActions(committed.value, [{ ...dispatchedAction, value: response.value }]);
                }
                return response;
            } catch {
                await recover().catch(() => undefined);
                return undefined;
            } finally {
                pending.value = pending.value.filter((item) => item.insight_id !== action.insight_id);
                payload.value = null;
            }
        });
        queue = result.then(
            () => undefined,
            () => undefined,
        );
        return result;
    }
    if (getCurrentInstance()) {
        provide(insightFeedActions, {
            enabled: () => Boolean(committed.value?.feed_enabled),
            isPending: (insightId) => pending.value.some((action) => action.insight_id === insightId),
            submit,
        });
    }
    return { feed, error, saving: computed(() => pending.value.length > 0) };
}
